/**
 * VHAKACHA PLATFORM CONFIG  (web app)
 * ============================================================
 * Reads the platform settings the admin console writes.
 *
 * Mirror of the commission half of
 *   provider app  →  src/lib/platform-config.ts
 *
 * WHY THIS EXISTS
 * ---------------
 * The admin console has a control that writes `app_config/main.commissionRate`
 * (see `setCommissionRate` in the provider app). Nothing in this app ever read
 * it, so every booking was written with the hardcoded DEFAULT_COMMISSION_RATE
 * regardless of what an admin had configured — the control silently did
 * nothing for new bookings, and `netMerchantPay` on the provider dashboard was
 * derived from a rate the platform had moved off.
 *
 * The coercion below must stay identical to `readCommissionRate` in the
 * provider app's `src/lib/platform-config.ts`. Both apps land on the same
 * number for the same document, or a traveller and a provider would see
 * different payouts for one booking.
 *
 * `app_config` is world-readable in firestore.rules, so this works for an
 * anonymous traveller.
 * ============================================================
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEFAULT_COMMISSION_RATE } from './pricing';
import { LOCATIONS as CODE_LOCATIONS } from '../data/locations';

export const SETTINGS_DOC = { collection: 'app_config', id: 'main' };

/** Highest accepted rate — a guard against a stray "10" meaning 1000%. */
export const MAX_COMMISSION_RATE = 0.5;

/** Cached for the session — the rate changes about as often as pricing policy. */
let inflight = null;

/**
 * Deliberately NOT `Number(x) || DEFAULT`: that turns a configured 0 into the
 * 10% default, so a launch partner set to 0% would silently be charged.
 * Commission is the platform's only real charge, so that is a billing bug.
 */
export function readCommissionRate(settings) {
  const raw = settings && settings.commissionRate;
  // An empty string is an unset field, not 0% — Number('') is 0, which would
  // silently zero the platform's only revenue.
  if (typeof raw === 'string' && raw.trim() === '') return DEFAULT_COMMISSION_RATE;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > MAX_COMMISSION_RATE) {
    return DEFAULT_COMMISSION_RATE;
  }
  return n;
}

/**
 * The platform take rate. Falls back to the default if the document is
 * missing or unreadable — a booking that cannot reach config is still worth
 * writing, and the rate it carries is the one both apps compute from.
 *
 * @returns {Promise<number>}
 */
export function getCommissionRate() {
  if (!inflight) {
    inflight = getDoc(doc(db, SETTINGS_DOC.collection, SETTINGS_DOC.id))
      .then((snap) => readCommissionRate(snap.exists() ? snap.data() : null))
      .catch((error) => {
        console.error('Failed to read app_config/main; using the default commission rate.', error);
        return DEFAULT_COMMISSION_RATE;
      });
  }
  return inflight;
}

/** Test seam — drops the cached read. */
export function resetCommissionRateCache() {
  inflight = null;
}

/* ============================================================
 * CANONICAL LOCATIONS
 *
 * The admin console edits `app_config/main.locations`; this app kept its own
 * copy in ../data/locations.js and never looked. Both lists happened to
 * agree, which is the only reason nothing broke — Destinations/index.js
 * filters with `p.location === state.name`, exact string equality, so the
 * first divergence would have made every listing in the renamed destination
 * silently undiscoverable rather than visibly wrong.
 *
 * `hydrateLocations` below must stay behaviourally identical to the function
 * of the same name in the provider app's src/lib/platform-config.ts. Same
 * document, same coercion, same fallbacks — or the two apps disagree about
 * what a destination is called.
 * ============================================================ */

/** Fallback pin. Victoria Falls, matching the provider app's default. */
const DEFAULT_COORDINATES = { latitude: -17.9243, longitude: 25.8572 };

const nonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

/**
 * Coerce whatever is in the document into canonical location records.
 *
 * Accepts either the full object form or a bare list of strings, because
 * `app_config/main` also carries `canonicalLocations` — the string list the
 * migration wrote — and a hand-edited document should not empty the picker.
 *
 * Returns null when there is nothing usable, so the caller can fall back
 * rather than render an empty destination list.
 */
export function hydrateLocations(raw) {
  if (!nonEmptyArray(raw)) return null;

  return raw
    .map((entry) => {
      if (typeof entry === 'string') {
        const known = CODE_LOCATIONS.find((l) => l.value === entry);
        return (
          known ?? {
            value: entry,
            label: entry,
            region: 'Other',
            coordinates: DEFAULT_COORDINATES,
            aliases: [],
          }
        );
      }
      if (!entry || typeof entry !== 'object') return null;

      const known = CODE_LOCATIONS.find((l) => l.value === entry.value);
      return {
        value: entry.value ?? '',
        label: entry.label ?? entry.value ?? '',
        region: entry.region ?? known?.region ?? 'Other',
        coordinates: entry.coordinates ?? known?.coordinates ?? DEFAULT_COORDINATES,
        aliases: entry.aliases ?? known?.aliases ?? [],
      };
    })
    .filter((l) => l && l.value);
}

/**
 * The same read, but ONLY for the rich object form — the shape `saveLocations`
 * writes and the admin config page edits.
 *
 * Null for a list of bare strings, which is how the two flat lists in the
 * document are told apart from an admin's edit. See `readLocations` below for
 * why that distinction decides which field wins.
 *
 * A mixed list counts as flat: it is a hand-edit, not something the config page
 * produced, and the admin labels this function exists to protect are not
 * reliably there.
 */
export function hydrateEditedLocations(raw) {
  if (!nonEmptyArray(raw)) return null;
  if (!raw.every((entry) => entry !== null && typeof entry === 'object')) return null;
  return hydrateLocations(raw);
}

/**
 * The canonical destinations, from config where possible and from code
 * otherwise.
 *
 * THREE SOURCES, IN THIS ORDER — and the order is the whole point.
 *
 *   1. `locations` IN THE RICH OBJECT FORM. What the admin config page writes,
 *      and the only source carrying admin-edited labels. It must win, or a
 *      rename would be silently reverted on the next read.
 *   2. `canonicalLocations`. The flat list of canonical values, kept in step by
 *      `saveLocations` on every save.
 *   3. `locations` AS A FLAT LIST. What is actually in production today: the
 *      pre-migration list, ten entries suffixed ", Zimbabwe" ('Victoria Falls,
 *      Zimbabwe'). Those match no `listings.location` — which is the bare name —
 *      and no key in the artwork table, so reading them first emptied the
 *      homepage carousel entirely and pointed every destination link at a name
 *      nothing is filed under. Ranked last so the stale field loses to the
 *      migration's, but kept, because a hand-edited flat list still beats no
 *      list at all.
 *
 * This ordering is mirrored in `mergeConfig` in the provider app's
 * src/lib/platform-config.ts. Both apps must resolve the same document to the
 * same destinations, or a provider files a listing under a name this app never
 * asks for.
 *
 * The fallback is load-bearing in the same way it is in the provider app: a
 * traveller on a bad connection, or an `app_config` that has been emptied,
 * must still get a browsable destination list. An empty one is not a degraded
 * experience, it is a homepage with nothing on it.
 */
export function readLocations(settings) {
  return (
    hydrateEditedLocations(settings?.locations) ??
    hydrateLocations(settings?.canonicalLocations) ??
    hydrateLocations(settings?.locations) ??
    CODE_LOCATIONS
  );
}

let locationsInflight = null;

/** @returns {Promise<Array<{value: string, label: string, region: string, coordinates: {latitude: number, longitude: number}, aliases: string[]}>>} */
export function getLocations() {
  if (!locationsInflight) {
    locationsInflight = getDoc(doc(db, SETTINGS_DOC.collection, SETTINGS_DOC.id))
      .then((snap) => readLocations(snap.exists() ? snap.data() : null))
      .catch((error) => {
        console.error('Failed to read app_config/main; using the built-in destinations.', error);
        return CODE_LOCATIONS;
      });
  }
  return locationsInflight;
}

/** Test seam — drops the cached read. */
export function resetLocationsCache() {
  locationsInflight = null;
}
